/* Copyright (c) 2020-present CloudIO Inc */

import { page as SamplePage } from './Popup_Help';
import { Platform } from '../../platform/Platform';
import { ItemType } from '../../registry/Base.type';
import {
  ComponentManager,
  ComponentManagerFn,
} from '../../registry/Component.type';
import { baseComponentManager } from '../../registry/Components';
import {
  getCustomChildItemTypes,
  getManager,
  getName,
} from '../../registry/Manager';
import { checkForFixedChild } from '../../utils/ManagerUtils';
import { defaultValue } from './Popup';

const name = 'Popup';

const PopupManager: ComponentManagerFn<'Popup'> = (
  platform: Platform,
): ComponentManager<'Popup'> => {
  return {
    ...baseComponentManager<'Popup'>(platform, 'Popup'),
    defaultValue,
    defaultChildren: ['FlexPanel'],
    isChildAllowed: (page, item, childType, context) => {
      if (
        !(
          [
            ...getCustomChildItemTypes('Popup'),
            'CanvasGrid',
            'Card',
            'Chartjs',
            'Code',
            'CombinationChart',
            'DataGrid',
            'DataStoreReference',
            'FlexPanel',
            'Form',
            'FunctionEditor',
            'HorizontalSplitPanel',
            'Mirror',
            'PopupActions',
            'RowPanel',
            'SearchForm',
            'TabPanel',
            'Toolbar',
            'TriggerOnPopupClose',
            'TriggerOnPopupOpen',
            'VerticalSplitPanel',
            'WorkflowViewer',
          ] as ItemType[]
        ).includes(childType)
      ) {
        return false;
      }
      if (childType === 'PopupActions') {
        const children = platform.getChildrenOfType(
          context.appUid,
          context.pageId,
          page,
          item,
          childType,
        );
        return children.length === 0
          ? true
          : `${getName(childType)} already added to ${name}`;
      }
      if (childType === 'Toolbar') {
        const children = platform.getChildrenOfType(
          context.appUid,
          context.pageId,
          page,
          item,
          childType,
        );
        return children.length === 0
          ? true
          : `${getName(childType)} already added to ${name}`;
      }
      return checkForFixedChild(platform, page, item, childType, name, context);
    },
    onChildMoveIn: ({ page, value }) => {
      // PopupActions must be the last child
      value.children = value.children.sort((a, b) => {
        const aItem = page[a];
        const bItem = page[b];
        if (aItem && bItem) {
          if (aItem.type === 'PopupActions') {
            return 1;
          }
          if (bItem.type === 'PopupActions') {
            return -1;
          }
          if (aItem.type === 'Toolbar') {
            return -1;
          }
          if (bItem.type === 'Toolbar') {
            return 1;
          }
        }
        return 0;
      });
    },
    props: {
      hideHeader: {
        label: 'Hide Header',
        type: 'boolean',
      },
      title: {
        label: 'Title',
        type: 'expression',
        returnType: async () => 'string',
      },
      modal: {
        label: 'Modal',
        type: 'boolean',
      },
      open: {
        label: 'Open',
        type: 'boolean',
      },
      isEmbedded: {
        label: 'Is Embedded',
        type: 'boolean',
        helpText: 'Check this if this popup is in loop',
      },
      fullScreen: {
        label: 'Full Screen',
        type: 'boolean',
      },
      bodyPadding: {
        label: 'Body Padding',
        type: 'boolean',
        category: 'Spacing',
      },
      popupWidth: {
        label: 'Width',
        type: 'number',
        min: 400,
        max: 1200,
        step: 40,
      },
      popupHeight: {
        label: 'Height',
        type: 'number',
        min: 200,
        max: 800,
        step: 40,
      },
      left: {
        label: 'Left',
        type: 'number',
        hidden: true,
      },
      top: {
        label: 'Top',
        type: 'number',
        hidden: true,
      },
    },
    category: 'Containers',
    name,
    canHaveChildren: () => true,
    layout: 'sizeAware',
    isPropertyVisible(prop, { hideHeader }): boolean {
      switch (prop) {
        case 'left':
        case 'top':
          return false;
        case 'title':
          return !hideHeader;
        default:
          return true;
      }
    },
    getDisplayText: (item, expanded, { appUid, pageId }) => {
      const page = platform.getPage(appUid, pageId);
      const itemIds = Object.keys(page);
      const usage = itemIds.filter((id) => {
        const itm = page[id];
        const mgr = getManager(itm.type);
        return mgr.isItemIdUsed(itm, item.itemId);
      });
      return usage.length
        ? `${item.props.title.value} (${usage.length} usage${
            usage.length > 1 ? 's' : ''
          })`
        : item.props.title.value;
    },
    icon: { iconName: 'window-restore' },
    generateShortId: (item) =>
      item.title?.type === 'text' ? item.title.value : '',
    description: `**Overview**<br/>
The Popup is a modal window that overlays the current page content. It is designed to capture user focus for specific, short-lived tasks—such as filling out a form, confirming an action, or viewing detailed record information—without navigating away from the main workspace.
By elevating content above the rest of the application, the Popup ensures that users address the immediate task before returning to their previous context.

**1. Visibility & Header Configuration**<br/>
The Popup comes with a built-in header bar to establish context.
* **Open:** A boolean flag that controls whether the popup is currently visible. This is typically bound to a variable or triggered by a button click (e.g., an "Edit" button).
* **Title:** The text displayed in the header bar. This supports expressions, allowing you to create dynamic titles like "Editing User: John Doe."
* **Hide Header:** If enabled, the header bar (including the title and default close button) is removed entirely. Use this for custom designs where you want to build your own header or create a completely borderless notification window.

**2. Interaction Mode (Modal)**<br/>
You can control how the user interacts with the background page while the popup is open.
* **Modal (True/Enabled):** The background is dimmed and non-interactive. Clicking outside the popup will not close it. This is critical for data integrity, ensuring users explicitly complete or cancel a task (like a "Save Changes" form) before leaving.
* **Modal (False/Disabled):** Clicking the background overlay automatically closes the popup. This is suitable for quick-view info panels.

**3. Sizing & Layout**<br/>
Popups can adapt to the amount of content they hold, from small confirmation dialogs to full-screen wizards.
* **Width & Height:** Sets the specific dimensions of the window. (Width: 400px – 1200px; Height: 200px – 800px).
* **Full Screen:** Overrides specific dimensions and forces the popup to occupy the entire viewport. This is ideal for complex workflows or mobile interfaces.
* **Body Padding:** Toggles the default spacing inside the popup body. Disable this if you are placing a "Full Width" component (like a Map or Data Grid) that should extend to the very edges of the window.

**4. Structural Composition**<br/>
The Popup enforces a specific internal layout to ensure consistency across the application. When you add children, they are automatically organized into three distinct zones:
* **Toolbar:** If added, this snaps to the very top, providing a space for auxiliary tools or menus.
* **Main Content:** The central area where your Forms, Grids, or Flex Panels reside.
* **Popup Actions:** If added, this component automatically snaps to the bottom footer. This is the dedicated space for "Save," "Cancel," or "Submit" buttons.
* **Is Embedded:** A technical flag used when rendering a popup inside a data loop (e.g., a unique popup for every row in a list). Enable this to ensure correct data context is passed to the popup.`,
    helpRenders: [
      {
        title: 'Popup Size',
        description:
          "Change the props and press 'Open Popup' button to see your changes in action.",
        page: SamplePage,
        exampleId: 'e1',
        clearParentChildren: false,
        visibleProps: ['fullScreen', 'popupHeight', 'popupWidth', 'title'],
        item: {
          children: ['popupActions'],
          itemId: 'popup',
          parent: 'root',
          props: {
            title: {
              type: 'text',
              value: 'Awesome Title',
            },
          },
          type: 'Popup',
        },
      },
      {
        title: 'Model Window',
        description:
          'When the Model prop is on, the user has to close the popup explicitly. Clicking outside the popup area will not automatically close it.',
        page: SamplePage,
        exampleId: 'e2',
        clearParentChildren: false,
        visibleProps: ['modal'],
        item: {
          children: ['popupActions'],
          itemId: 'popup',
          parent: 'root',
          props: {
            title: {
              type: 'text',
              value: 'Awesome Title',
            },
            modal: true,
          },
          type: 'Popup',
        },
      },
    ],
  };
};

export default PopupManager;
